import { useQuery } from '@tanstack/react-query';

import { buildTournamentsApiUrl } from '@shared/config/tournaments';

import { TournamentEvent, GoogleSheetsResponse } from '@types';

const TOURNAMENTS_QUERY_KEY = ['tournaments'];

// Event colors for calendar display
const EVENT_COLORS = [
	{ backgroundColor: '#5A5A5A', color: '#FFFFFF' },
	{ backgroundColor: '#333217', color: '#FFEB31' },
	{ backgroundColor: '#3A2159', color: '#AE6FFF' },
	{ backgroundColor: '#11362C', color: '#07EF9C' },
	{ backgroundColor: '#19334F', color: '#1D92FF' }
];

/**
 * Parse raw Google Sheets data into TournamentEvent objects
 */
const parseGoogleSheetsData = (data: GoogleSheetsResponse): TournamentEvent[] => {
	if (!data.values || data.values.length === 0) {
		console.error('Empty data from Google Sheets');
		return [];
	}

	const rows = data.values.slice(1);

	return rows.map((event, index) => {
		const [dayStart, monthStart, yearStart] = event[0]?.trim().split('.') || [];
		const [dayEnd, monthEnd, yearEnd] = event[1]?.trim().split('.') || [];

		const startDate = dayStart && monthStart && yearStart ? new Date(`${yearStart}-${monthStart}-${dayStart}`) : null;

		const endDate = dayEnd && monthEnd && yearEnd ? new Date(`${yearEnd}-${monthEnd}-${dayEnd}`) : null;

		if (startDate && isNaN(startDate.getTime())) {
			console.warn(`Invalid start date: ${event[0]}`);
		}
		if (endDate && isNaN(endDate.getTime())) {
			console.warn(`Invalid end date: ${event[1]}`);
		}

		const colorIndex = index % EVENT_COLORS.length;
		const colors = EVENT_COLORS[colorIndex];

		return {
			startDate: startDate && !isNaN(startDate.getTime()) ? startDate : null,
			endDate: endDate && !isNaN(endDate.getTime()) ? endDate : null,
			name: event[2]?.trim() || 'Без названия',
			gameType: event[3]?.trim() || 'Не указан',
			prize: event[4]?.trim() || 'Не указано',
			description: event[5]?.trim() || '',
			img: event[6]?.trim() || '',
			url: event[7]?.trim() || '#',
			imgQuest: event[8]?.trim(),
			nameQuest: event[9]?.trim(),
			statusQuest: event[10]?.trim(),
			descriptionOne: event[11]?.trim(),
			descriptionTwo: event[12]?.trim(),
			tegQuest: event[13]?.trim(),
			buttonUrl: event[14]?.trim(),
			backgroundColor: colors.backgroundColor,
			color: colors.color
		};
	});
};

/**
 * Hook to fetch tournaments data from Google Sheets
 */
export const useQueryTournaments = () => {
	return useQuery<TournamentEvent[]>({
		queryKey: TOURNAMENTS_QUERY_KEY,
		queryFn: async () => {
			const url = buildTournamentsApiUrl();

			try {
				const response = await fetch(url);

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				const data: GoogleSheetsResponse = await response.json();
				console.log(response);
				return parseGoogleSheetsData(data);
			} catch (error) {
				console.error('Error fetching tournaments data:', error);
				throw error;
			}
		},
		staleTime: 5 * 60 * 1000,
		retry: 2
	});
};

export { TOURNAMENTS_QUERY_KEY };
